// frontend/src/hooks/useReorderLevelsData.js
//
// The reads and writes of the reorder-levels surface (`/api/reorder-levels/`,
// backend/forecasting/reorder_levels.py): the store's status, the welcome
// dismissal, the grid's bulk save and "Set for all".
//
// The two writes set a placement's minimum stock and a forecast override
// (expected monthly sales), and name those two kinds of data.

import { useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '../contexts/AuthContext';
import { BULK_CHUNK_SIZE } from '../datahealth/resolveIssue';
import { apiJson } from '../queries/api';
import { DATA } from '../queries/dataChanged';
import { reorderLevels as reorderLevelsKey } from '../queries/keys';
import { useApiMutation } from '../queries/useApiMutation';
import { useChangeEffect } from './useChangeEffect';
import { useInAppSyncStatus } from './useInAppSyncStatus';

const CHANGES = [DATA.inventory, DATA.forecasts];

/**
 * `{current_month, has_forecast_demand, show_welcome}` for the selected
 * store, or null until the read lands and when it failed. `show_welcome` is
 * the server's whole rule; nothing here recomposes it. `current_month` is the
 * store-local month (an ISO date) the grid's expected-sales cell shows.
 *
 * Re-read when the store's first forecast finishes: `show_welcome` needs a
 * generated forecast, and that arrives while the app is open.
 */
export function useReorderLevelsStatus() {
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: reorderLevelsKey.status(storeId),
    queryFn: () => apiJson.get('/api/reorder-levels/status/'),
    enabled: !!storeId,
  });

  const forecastsGenerated = useInAppSyncStatus()?.forecastsGenerated === true;
  const reread = useCallback(() => {
    if (forecastsGenerated) {
      queryClient.invalidateQueries({ queryKey: reorderLevelsKey.status(storeId) });
    }
  }, [forecastsGenerated, queryClient, storeId]);
  useChangeEffect(forecastsGenerated, reread);

  useEffect(() => {
    if (query.error) console.error('Reorder levels status read failed:', query.error.message);
  }, [query.error]);

  return query.data ?? null;
}

/**
 * Record that the welcome was seen. The endpoint answers the status body, so
 * the response replaces the cached status and the popup closes on it.
 */
export function useDismissReorderLevelsWelcome() {
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;
  const queryClient = useQueryClient();

  return useApiMutation({
    mutationFn: () => apiJson.post('/api/reorder-levels/dismiss-welcome/'),
    changes: [],
    onSuccess: (data) => queryClient.setQueryData(reorderLevelsKey.status(storeId), data),
  });
}

/**
 * Save the grid's edited rows.
 * Variables: `{ items }`, `[{ id, fields }]` of any length — sent in requests
 * of at most `BULK_CHUNK_SIZE`, the server's ceiling per batch write.
 * Returns one `{ results, summary }` over every request.
 */
export function useReorderLevelsBulkUpdate() {
  return useApiMutation({
    mutationFn: async ({ items }) => {
      const results = [];
      const summary = { total: 0, succeeded: 0, failed: 0 };
      for (let start = 0; start < items.length; start += BULK_CHUNK_SIZE) {
        const data = await apiJson.post('/api/reorder-levels/bulk-update/', {
          items: items.slice(start, start + BULK_CHUNK_SIZE),
        });
        results.push(...data.results);
        summary.total += data.summary.total;
        summary.succeeded += data.summary.succeeded;
        summary.failed += data.summary.failed;
      }
      return { results, summary };
    },
    changes: CHANGES,
  });
}

/**
 * Write one value to every row the grid's filters leave.
 * Variables: the request body — `min_inventory_units` and/or
 * `expected_monthly_sales`, plus the grid's `search` and `location`.
 * Returns the server's counts (`pairs`, `skipped_promotion`, …).
 */
export function useReorderLevelsApplyToAll() {
  return useApiMutation({
    mutationFn: (body) => apiJson.post('/api/reorder-levels/apply-to-all/', body),
    changes: CHANGES,
  });
}
